/**
 * CR403/CR404: Category Detail Panel - right side of split view
 * CR404: Added Branding section, renamed "slug" to "Category Key"
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Save, Trash2, Loader2, FolderOpen, AlertTriangle } from "lucide-react";
import type { MerchantCategoryRow } from "@/hooks/useMerchantCategories";
import { DeleteCategoryModal } from "./DeleteCategoryModal";
import { CategoryBrandingCard } from "./CategoryBrandingCard";

interface CategoryDetailProps {
  category: MerchantCategoryRow;
  allCategories: MerchantCategoryRow[];
  onUpdate: (params: { 
    categoryId: string; 
    name?: string; 
    slug?: string;
    iconUrl?: string | null;
    iconSvg?: string | null;
    iconSource?: string | null;
    iconAttribution?: string | null;
  }) => Promise<void>;
  onDelete: (params: { oldCategoryId: string; newCategoryId: string; clearSourceOverrides?: boolean }) => Promise<unknown>;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

/**
 * Get icon to render based on priority: SVG > URL > placeholder
 */
function getCategoryIcon(category: MerchantCategoryRow): React.ReactNode {
  if (category.icon_svg) {
    return (
      <div
        className="w-6 h-6"
        dangerouslySetInnerHTML={{ __html: category.icon_svg }}
      />
    );
  }
  if (category.icon_url) {
    return (
      <img
        src={category.icon_url}
        alt={category.name}
        className="w-6 h-6 object-contain"
      />
    );
  }
  return <FolderOpen className="h-6 w-6 text-muted-foreground" />;
}

export function CategoryDetail({
  category,
  allCategories,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}: CategoryDetailProps) {
  const [name, setName] = useState(category.name);
  const [slug, setSlug] = useState(category.slug);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Reset form when category changes
  useEffect(() => {
    setName(category.name);
    setSlug(category.slug);
  }, [category.id, category.name, category.slug]);

  const hasChanges = name !== category.name || slug !== category.slug;
  const isInUse = category.merchants_count > 0 || category.sources_override_count > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    await onUpdate({
      categoryId: category.id,
      name: name !== category.name ? name : undefined,
      slug: slug !== category.slug ? slug : undefined,
    });
  };

  const handleDeleteConfirm = async (newCategoryId: string, clearOverrides: boolean) => {
    await onDelete({
      oldCategoryId: category.id,
      newCategoryId,
      clearSourceOverrides: clearOverrides,
    });
    setShowDeleteModal(false);
  };

  return (
    <>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
              {getCategoryIcon(category)}
            </div>
            <div>
              <h2 className="text-xl font-semibold">{category.name}</h2>
              <p className="text-sm text-muted-foreground">Key: {category.slug}</p>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
            <CardDescription>Edit category name and key</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cat_name">Name</Label>
              <Input
                id="cat_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Category name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cat_slug">Category Key</Label>
              <Input
                id="cat_slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="category-key"
                className="font-mono"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={!hasChanges || isUpdating}
              className="w-full"
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </CardContent>
        </Card>

        {/* Branding Section */}
        <CategoryBrandingCard
          category={category}
          onUpdate={onUpdate}
          isUpdating={isUpdating}
        />

        {/* Usage Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Usage</CardTitle>
            <CardDescription>How this category is used</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-2xl font-bold">{category.merchants_count}</div>
                <div className="text-xs text-muted-foreground">Merchants</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-bold">{category.sources_effective_count}</div>
                <div className="text-xs text-muted-foreground">Effective Sources</div>
              </div>
              <div className="space-y-1">
                <div className="text-lg font-medium">{category.sources_inherited_count}</div>
                <div className="text-xs text-muted-foreground">Inherited</div>
              </div>
              <div className="space-y-1">
                <div className="text-lg font-medium">{category.sources_override_count}</div>
                <div className="text-xs text-muted-foreground">Overrides</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Delete Section */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              {isInUse
                ? "This category is in use. Deleting requires reassigning merchants/sources."
                : "This category is not in use and can be deleted."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteModal(true)}
              disabled={isDeleting}
              className="w-full"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Category
            </Button>
          </CardContent>
        </Card>
      </div>

      <DeleteCategoryModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        category={category}
        allCategories={allCategories}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </>
  );
}
